import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import { Icon, Tabs, Tab } from "@material-ui/core";
import ApiEndpoints from "../../../../constants/api_endpoints";
import store from "../../../../store";
import { setError, setSuccess, setLoader } from "../../../../actions/action-creators";
import { copyCodeToClipboard } from "../../../../actions";
import ConfirmAction from "../../../global/confirm-action";
import ConfigEditor from "./config_editor";
import { StatusBadge } from "./sections";
import { IconButton } from "./common_controls";
import { parseConfigResponse, serializeConfigModel, validateConfigModel, buildEmptyConfig, configModelToJsonView, splitIdentifierColumns } from "./config_model";
import { describeConfigPlainEnglish, CATEGORY_META } from "./plain_english";

const CategoryBadge = ({ categories }) => (
  <div className="d-flex flex-wrap justify-content-end" style={{ gap: 6 }}>
    {(categories && categories.length ? categories : ["other"]).map(cat => {
      const meta = CATEGORY_META[cat] || CATEGORY_META.other;
      return (
        <span key={cat} className="small font-weight-bold text-nowrap" style={{ color: meta.color, backgroundColor: meta.bg, borderRadius: 10, padding: "2px 10px" }}>
          {meta.label}
        </span>
      );
    })}
  </div>
);

const isPrivilegedUser = () => {
  const roles = ((store.getState().user || {}).UserRole || []).map(r => r.role_id);
  return roles.indexOf(1) !== -1 || roles.indexOf(3) !== -1;
};

// This screen renders inside a Modal whose ModalBody (not the window) scrolls, so the
// IntersectionObserver watching the footer needs that scrolling ancestor as its root —
// walk up until we find the element that actually has overflow set.
const getScrollParent = el => {
  let parent = el && el.parentElement;
  while (parent) {
    if (/(auto|scroll)/.test(window.getComputedStyle(parent).overflowY)) return parent;
    parent = parent.parentElement;
  }
  return null;
};

const configLabel = config => (config.mappings[0] && config.mappings[0].email_id) || "Untitled configuration";

const summarizeConfig = config => {
  const ruleCount = (config.rules || []).length;
  const dupCount = (config.duplicateConfiguration || []).length;
  const identifierCols = splitIdentifierColumns(config.identifier_column);
  // identifier_input_id set means the report's column value was saved into that task-form input
  // as an application no. for matching — not necessarily the literal mobile number.
  const identifierLabel = config.identifier_input_id
    ? `Application No. matching (Input #${config.identifier_input_id})`
    : Number(config.identifier) === 1
    ? "Mobile Number matching"
    : "Reference ID matching";
  const parts = [
    identifierCols.length ? `${identifierLabel} via ${identifierCols.join(", ")}` : `${identifierLabel} (no column set)`,
    `${ruleCount} rule${ruleCount === 1 ? "" : "s"}`
  ];
  if (dupCount) parts.push(`${dupCount} duplicate-handling rule${dupCount === 1 ? "" : "s"}`);
  return parts.join(" · ");
};

// One row per mail mapping, not per config — a config with several mapped emails (Mail Routing)
// shows up as several rows sharing the same config id, so each email is visible in the list.
const flattenRows = configs => {
  const rows = [];
  configs.forEach(config => {
    if (config.mappings && config.mappings.length) config.mappings.forEach(mapping => rows.push({ config, mapping }));
    else rows.push({ config, mapping: null });
  });
  return rows;
};

const VerificationConfig = ({ jobTask, onClose }) => {
  const formId = jobTask && jobTask.form_id;
  const [configs, setConfigs] = useState([]);
  const [mode, setMode] = useState("list"); // "list" | "view" | "edit"
  const [activeId, setActiveId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewTab, setViewTab] = useState(0); // 0 = Plain English, 1 = JSON
  const [footerVisible, setFooterVisible] = useState(true);
  const footerRef = useRef(null);
  const canEdit = isPrivilegedUser();

  useEffect(() => {
    if (mode !== "edit" || !footerRef.current) return undefined;
    const root = getScrollParent(footerRef.current);
    const observer = new IntersectionObserver(([entry]) => setFooterVisible(entry.isIntersecting), { root, threshold: 0 });
    observer.observe(footerRef.current);
    return () => observer.disconnect();
  }, [mode, activeId]);

  const fetchConfigs = async () => {
    store.dispatch(setLoader(true));
    try {
      const resp = await ApiEndpoints.VerificationConfig.fetch({ form_id: formId });
      setConfigs((resp.data.data || []).map(parseConfigResponse));
    } catch (e) {
      store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
    }
    store.dispatch(setLoader(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchConfigs(); }, []);

  const activeConfig = configs.find(c => c.config_id === activeId) || (activeId === "NEW" ? configs.find(c => c.config_id == null) : null);

  const updateActiveConfig = updated => {
    setConfigs(prev => prev.map(c => (c === activeConfig ? updated : c)));
    setDirty(true);
  };

  const openCreate = () => {
    const draft = buildEmptyConfig(formId);
    setConfigs(prev => [...prev, draft]);
    setActiveId("NEW");
    setMode("edit");
    setDirty(true);
  };

  const openView = config => { setActiveId(config.config_id); setMode("view"); setViewTab(0); };
  const openEdit = config => { setActiveId(config.config_id || "NEW"); setMode("edit"); setDirty(false); };

  const backToList = () => {
    if (dirty && mode === "edit" && !window.confirm("You have unsaved changes. Discard them and go back?")) return;
    if (activeId === "NEW") setConfigs(prev => prev.filter(c => c.config_id != null));
    setMode("list");
    setActiveId(null);
    setDirty(false);
  };

  const save = async () => {
    if (!activeConfig) return;
    const errors = validateConfigModel(activeConfig);
    if (errors.length) {
      store.dispatch(setError({ show: true, message: errors[0] }));
      return;
    }
    const payload = serializeConfigModel(activeConfig);
    store.dispatch(setLoader(true));
    try {
      const isNew = !activeConfig.config_id;
      const resp = isNew ? await ApiEndpoints.VerificationConfig.create(payload) : await ApiEndpoints.VerificationConfig.edit(payload);
      const saved = resp.data.data;
      if (saved && saved.warnings && saved.warnings.length) store.dispatch(setError({ show: true, message: saved.warnings[0] }));
      store.dispatch(setSuccess({ show: true, message: "Configuration saved successfully" }));
      await fetchConfigs();
      setMode("list");
      setActiveId(null);
      setDirty(false);
    } catch (e) {
      store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : "Unable to save configuration. Please try again." }));
    }
    store.dispatch(setLoader(false));
  };

  const deleteConfig = async () => {
    const target = confirmDelete;
    setConfirmDelete(null);
    if (!target || !target.config || !target.config.config_id) return;
    const { config, mapping } = target;
    const remainingMappings = mapping ? config.mappings.filter(m => m !== mapping) : [];
    store.dispatch(setLoader(true));
    try {
      if (remainingMappings.length) {
        await ApiEndpoints.VerificationConfig.edit(serializeConfigModel({ ...config, mappings: remainingMappings }));
        store.dispatch(setSuccess({ show: true, message: "Mail mapping removed" }));
      } else {
        await ApiEndpoints.VerificationConfig.deactivate({ config_id: config.config_id });
        store.dispatch(setSuccess({ show: true, message: "Configuration deleted" }));
      }
      await fetchConfigs();
    } catch (e) {
      store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }));
    }
    store.dispatch(setLoader(false));
  };

  return (
    <div>
      {mode === "list" && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="small text-muted">{configs.length} configuration{configs.length === 1 ? "" : "s"}</div>
            {canEdit && (
              <Button className="buttonred" onClick={openCreate}>
                <Icon fontSize="small" className="align-middle">add</Icon> Create Configuration
              </Button>
            )}
          </div>

          {!configs.length && <div className="text-muted small mb-3">No verification configuration exists for this task yet.</div>}

          {!!configs.length && (
            <div className="border rounded" style={{ borderRadius: 10, border: "1px solid #e2e8f0", overflow: "hidden" }}>
              {flattenRows(configs).map(({ config: c, mapping: m }, i) => (
                <div
                  key={`${c.config_id || "NEW"}_${m ? m.id : "none"}`}
                  className={"d-flex align-items-center px-3 py-3" + (i ? " border-top" : "")}
                  style={{ borderColor: "#e2e8f0", cursor: "pointer" }}
                  onClick={() => openView(c)}
                >
                  <div className="flex-grow-1 mr-3" style={{ minWidth: 0 }}>
                    <div className="font-weight-bold text-dark text-truncate">
                      {c.config_id != null && <span className="text-muted font-weight-normal mr-2">#{c.config_id}</span>}
                      {(m && m.email_id) || "Untitled configuration"}
                    </div>
                    <div className="small text-muted text-truncate">{summarizeConfig(c)}</div>
                  </div>
                  <div className="text-muted small mr-4 text-nowrap" style={{ minWidth: 100 }}>
                    {c.created_on ? new Date(c.created_on).toLocaleDateString() : "-"}
                  </div>
                  <div className="mr-4 text-nowrap"><StatusBadge isActive={c.is_active} /></div>
                  {canEdit && (
                    <div className="d-flex align-items-center" onClick={e => e.stopPropagation()}>
                      <IconButton icon="edit" onClick={() => openEdit(c)} title="Edit configuration" />
                      <span className="ml-3"><IconButton icon="delete" danger onClick={() => setConfirmDelete({ config: c, mapping: m })} title="Delete" /></span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === "view" && activeConfig && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button color="link" className="p-0 text-muted" onClick={backToList}>
              <Icon fontSize="small" className="align-middle">arrow_back</Icon> Back to list
            </Button>
            {canEdit && (
              <Button className="buttonred" onClick={() => openEdit(activeConfig)}>
                <Icon fontSize="small" className="align-middle">edit</Icon> Edit Configuration
              </Button>
            )}
          </div>
          <div className="mb-2">
            <strong>{configLabel(activeConfig)}</strong> <StatusBadge isActive={activeConfig.is_active} />
          </div>

          <Row noGutters className="border-bottom mb-3">
            <Col>
              <Tabs value={viewTab} onChange={(e, v) => setViewTab(v)} indicatorColor="primary" textColor="primary">
                <Tab label="Plain English" />
                <Tab label="JSON" />
              </Tabs>
            </Col>
          </Row>

          {viewTab === 0 ? (
            <div>
              {(() => {
                const plain = describeConfigPlainEnglish(activeConfig);
                return (
                  <div>
                    {!plain.rules.length && <div className="small text-muted mb-3">No verification rules configured.</div>}
                    {plain.rules.map((rule, i) => (
                      <div key={i} className="border rounded p-3 mb-2" style={{ borderRadius: 8, border: "1px solid #e2e8f0" }}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <span className="font-weight-bold small">Rule {i + 1}</span>
                          <CategoryBadge categories={rule.categories} />
                        </div>
                        <div className="small mb-1">
                          <strong>If</strong> {rule.conditions.length ? rule.conditions.join(" AND ") : "(no conditions — always applies)"}
                        </div>
                        <div className="small text-muted"><strong>Then</strong> {rule.action}</div>
                      </div>
                    ))}

                    <div className="font-weight-bold small mt-3 mb-1">Mail Routing</div>
                    {!plain.mail_routing.length && <div className="small text-muted">No client emails mapped.</div>}
                    {plain.mail_routing.map((m, i) => (
                      <div key={i} className="small text-muted">{m.email || "(no email)"} — Form ID(s): {m.forms.length ? m.forms.join(", ") : "—"}</div>
                    ))}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="border rounded" style={{ borderRadius: 10, border: "1px solid #e2e8f0", overflow: "hidden" }}>
              <div className="d-flex justify-content-end p-2 border-bottom" style={{ backgroundColor: "#f0f2f5" }}>
                <Button size="sm" color="link" className="p-0" onClick={() => copyCodeToClipboard(JSON.stringify(configModelToJsonView(activeConfig), null, 2))}>
                  <Icon fontSize="small" className="align-middle">content_copy</Icon> Copy
                </Button>
              </div>
              <pre className="p-3 mb-0 bg-white small" style={{ maxHeight: 480, overflow: "auto", fontFamily: "monospace" }}>
                {JSON.stringify(configModelToJsonView(activeConfig), null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      {mode === "edit" && activeConfig && (
        <div>
          {!footerVisible && (
            <div
              className="d-flex justify-content-end pb-2 mb-3 border-bottom bg-white"
              style={{ position: "sticky", top: 0, zIndex: 2 }}
            >
              <Button color="secondary" outline className="mr-2" onClick={backToList}>Cancel</Button>
              {canEdit && <Button className="buttonred" onClick={save}>Save Configuration</Button>}
            </div>
          )}
          <h6 className="mb-3">{activeConfig.config_id ? `Edit Configuration — ${configLabel(activeConfig)}` : "Create Configuration"}</h6>
          <fieldset disabled={!canEdit}>
            <ConfigEditor config={activeConfig} onChange={updateActiveConfig} defaultFormId={formId} />
          </fieldset>
          <div ref={footerRef} className="mt-3 pt-3 border-top d-flex justify-content-end">
            <Button color="secondary" outline className="mr-2" onClick={backToList}>Cancel</Button>
            {canEdit && <Button className="buttonred" onClick={save}>Save Configuration</Button>}
          </div>
        </div>
      )}

      {mode === "list" && (
        <div className="mt-3 pt-3 border-top d-flex justify-content-end">
          <Button color="secondary" outline onClick={onClose}>Close</Button>
        </div>
      )}

      <ConfirmAction
        isOpen={!!confirmDelete}
        message={
          confirmDelete
            ? confirmDelete.mapping && confirmDelete.config.mappings.length > 1
              ? `Remove "${confirmDelete.mapping.email_id}" from this configuration? Configuration #${confirmDelete.config.config_id} will keep running for its other mail mapping(s).`
              : `Are you sure you want to delete this configuration${confirmDelete.mapping ? ` for "${confirmDelete.mapping.email_id}"` : ""}?`
            : ""
        }
        callback={deleteConfig}
        onClose={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default VerificationConfig;
